import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTheme, Box, Typography } from '@mui/material';
import { Plot } from '../../pages/components/Plot';
import { IV_CAP, SurfaceView, VolSurfaceGrids } from './useVolSurfaceGrids';

interface VolatilitySurface3DProps {
  surfaces: VolSurfaceGrids;
  view: SurfaceView;
}

// Ignore sub-pixel changes so a coalesced resize doesn't re-render every frame.
const SIZE_CHANGE_TOLERANCE_PX = 2;

const VolatilitySurface3D = ({ surfaces, view }: VolatilitySurface3DProps) => {
  const theme = useTheme();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [plotSize, setPlotSize] = useState({ width: 0, height: 0 });

  const hasDataForView =
    view === 'current' ||
    (view === 'projected' ? surfaces.projectedZValues : surfaces.gapZValues)
      .flat()
      .some((v) => !Number.isNaN(v));

  // Plotly's gl3d canvas swallows wheel events for camera dolly-zoom even
  // with scrollZoom disabled, which blocks page scroll wherever the chart
  // sits under the cursor. Intercepting in the capture phase (native
  // listener, ahead of Plotly's own) and only stopping propagation - never
  // preventDefault - lets the browser's native scroll still happen while
  // keeping Plotly from seeing the event at all. Re-runs when
  // hasDataForView flips true, since the ref target only exists once the
  // chart (rather than the no-data placeholder) is actually rendered.
  useEffect(() => {
    const node = wrapperRef.current;
    if (node == null) {
      return undefined;
    }
    const stopForPlotly = (e: WheelEvent) => e.stopPropagation();
    node.addEventListener('wheel', stopForPlotly, { capture: true });
    return () => node.removeEventListener('wheel', stopForPlotly, true);
  }, [hasDataForView]);

  // Plotly's canvas only redraws on a real window resize event - a CSS-only
  // height change (our fullscreen toggle) never fires one. Measure the
  // container directly instead, so any cause of a resize is caught.
  useLayoutEffect(() => {
    const node = wrapperRef.current;
    if (node == null) {
      return undefined;
    }
    let rafId: number | null = null;
    let pending = { width: node.clientWidth, height: node.clientHeight };
    setPlotSize(pending);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry == null) {
        return;
      }
      pending = {
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      };
      if (rafId == null) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          setPlotSize((prev) =>
            Math.abs(prev.width - pending.width) >= SIZE_CHANGE_TOLERANCE_PX ||
            Math.abs(prev.height - pending.height) >= SIZE_CHANGE_TOLERANCE_PX
              ? pending
              : prev,
          );
        });
      }
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [hasDataForView]);

  const plotConfig = useMemo(() => {
    const {
      strikeGrid,
      dteValues,
      currentZValues,
      projectedZValues,
      gapZValues,
    } = surfaces;

    if (view === 'gap') {
      const flatGaps = gapZValues.flat().filter((v) => !Number.isNaN(v));
      const maxAbsGap = Math.max(0.01, ...flatGaps.map((v) => Math.abs(v)));

      const plotData = [
        {
          type: 'surface' as const,
          x: strikeGrid,
          y: dteValues,
          z: gapZValues,
          colorscale: 'RdBu',
          reversescale: true,
          cmin: -maxAbsGap,
          cmax: maxAbsGap,
          surfacecolor: gapZValues,
          showscale: true,
          colorbar: { title: 'Gap', tickformat: '+.1%' },
          hovertemplate:
            '<b>Strike</b>: %{x:.0f}<br><b>DTE</b>: %{y:.0f}<br><b>Gap</b>: %{z:+.1%}<extra></extra>',
        },
      ];

      const layoutConfig = {
        margin: { t: 10, l: 10, r: 10, b: 30 },
        ...(plotSize.width > 0 && plotSize.height > 0 ? plotSize : {}),
        paper_bgcolor: theme.palette.background.paper,
        plot_bgcolor: theme.palette.background.paper,
        scene: {
          xaxis: {
            title: 'Strike',
            tickformat: '.2s',
            gridcolor: theme.palette.grey[300],
            backgroundcolor: theme.palette.background.paper,
          },
          yaxis: {
            title: 'DTE',
            tickformat: '.0f',
            gridcolor: theme.palette.grey[300],
            backgroundcolor: theme.palette.background.paper,
          },
          zaxis: {
            title: 'IV Gap',
            tickformat: '+.1%',
            range: [-maxAbsGap, maxAbsGap],
            gridcolor: theme.palette.grey[300],
            backgroundcolor: theme.palette.background.paper,
          },
          // Empirically tuned - Plotly's 3D scenes have no automargin, so a
          // tighter eye clips the Strike axis title/ticks past this point.
          camera: {
            eye: { x: 1.48, y: 1.48, z: 1.18 },
            center: { x: 0, y: 0, z: -0.2 },
          },
          aspectmode: 'auto' as const,
        },
      };

      return { plotData, layoutConfig };
    }

    const zValues = view === 'projected' ? projectedZValues : currentZValues;

    const plotData = [
      {
        type: 'surface' as const,
        x: strikeGrid,
        y: dteValues,
        z: zValues,
        colorscale: theme.palette.mode === 'dark' ? 'Plasma' : 'Viridis',
        cmin: 0,
        cmax: IV_CAP,
        surfacecolor: zValues,
        showscale: true,
        colorbar: { title: 'IV', tickformat: '.0%' },
        hovertemplate:
          '<b>Strike</b>: %{x:.0f}<br><b>DTE</b>: %{y:.0f}<br><b>IV</b>: %{z:.1%}<extra></extra>',
      },
    ];

    const layoutConfig = {
      margin: { t: 10, l: 10, r: 10, b: 30 },
      ...(plotSize.width > 0 && plotSize.height > 0 ? plotSize : {}),
      paper_bgcolor: theme.palette.background.paper,
      plot_bgcolor: theme.palette.background.paper,
      scene: {
        xaxis: {
          title: 'Strike',
          tickformat: '.2s',
          gridcolor: theme.palette.grey[300],
          backgroundcolor: theme.palette.background.paper,
        },
        yaxis: {
          title: 'DTE',
          tickformat: '.0f',
          gridcolor: theme.palette.grey[300],
          backgroundcolor: theme.palette.background.paper,
        },
        zaxis: {
          title: 'IV',
          tickformat: '.1%',
          range: [0, IV_CAP],
          gridcolor: theme.palette.grey[300],
          backgroundcolor: theme.palette.background.paper,
        },
        // Empirically tuned - Plotly's 3D scenes have no automargin, so a
        // tighter eye clips the Strike axis title/ticks past this point.
        camera: {
          eye: { x: 1.48, y: 1.48, z: 1.18 },
          center: { x: 0, y: 0, z: -0.2 },
        },
        aspectmode: 'auto' as const,
      },
    };

    return { plotData, layoutConfig };
  }, [surfaces, view, theme.palette, plotSize]);

  if (!hasDataForView) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="text.secondary">
          No projected volatility data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box ref={wrapperRef} sx={{ width: '100%', height: '100%' }}>
      <Plot
        data={plotConfig.plotData}
        layout={plotConfig.layoutConfig}
        style={{ width: '100%', height: '100%' }}
        config={{
          displayModeBar: false,
          scrollZoom: false,
        }}
      />
    </Box>
  );
};

export default VolatilitySurface3D;
